import { ref, computed } from 'vue';
import type { ComputedRef } from 'vue';

import { defineStore } from 'pinia';

import ApiBaseline from '@/api/ApiBaseline';
import Consts from '@/api/Consts';

import {
	normalizeBaseStationStatus,
	normalizeBaseStationOnlineStatus,
	normalizeBaseStationLiveStatus,
} from '@/utils/baseStationsUtils';

import { showErrorMessage } from '@/components/common/errorMessage';

import type {
	BaselineAction,
	BaselineChangeStatusResponse,
	BaselineDetailsRaw,
	BaselineSettingsRaw,
	BaselineStationRaw,
} from '@/api/ApiTypes';
import type { BaselineStationStored, BaselineStoreItem } from '@/stores/types/mapStoreContracts';


const toStoredStation = (station: BaselineStationRaw): BaselineStationStored => ({
	id: station.id,
	mntpnt_int: station.mntpnt_int,
	status: normalizeBaseStationStatus(station.status),
	status_live: normalizeBaseStationLiveStatus(station.status_live),
	deleted: station.deleted,
});


const toStoredBaseline = (baseline: BaselineDetailsRaw): BaselineStoreItem => ({
	id: baseline.id,
	base: toStoredStation(baseline.base),
	rover: toStoredStation(baseline.rover),
	base_mntpnt_int: baseline.base_mntpnt_int,
	rover_mntpnt_int: baseline.rover_mntpnt_int,
	description: baseline.description,
	status: normalizeBaseStationStatus(baseline.status),
	status_online: normalizeBaseStationOnlineStatus(baseline.status_online),
	updating: baseline.updating,
	deleted: baseline.deleted,
	nmea_port_ext: baseline.nmea_port_ext,
	message: baseline.message,
	selected_gnss: Consts.sortGnssCodes(baseline.selected_gnss ?? []),
});


const appendBaselineId = (
	result: Map<number, number[]>,
	stationId: number,
	baselineId: number,
): void => {
	const existing = result.get(stationId);
	if (existing !== undefined) {
		existing.push(baselineId);
		return;
	}
	result.set(stationId, [baselineId]);
};


export const useBaselinesStore = defineStore('baselines', () => {
	const items = ref<BaselineStoreItem[]>([]);
	const settings = ref<Partial<BaselineSettingsRaw>>({});
	const isLoading = ref(true);

	const update = (itemsValue: BaselineDetailsRaw[], settingsValue: BaselineSettingsRaw): void => {
		items.value = itemsValue.map(toStoredBaseline);
		settings.value = settingsValue;
		isLoading.value = false;
	};

	const handleChangeStatus = (
		onSuccess?: (responseData: BaselineChangeStatusResponse) => void,
	) => (
		responseData: BaselineChangeStatusResponse,
	): void => {
		if (responseData.status === 'ok') {
			if (onSuccess != null) {
				onSuccess(responseData);
			}
		} else {
			showErrorMessage(responseData.errors);
		}
	};

	// ChangeStatusActionBar always passes [singleId] (same signature as
	// baseStations.changeStatuses, which also has a group endpoint).
	// POST /baseline/change_status/ takes one id; there is no group API.
	const changeStatuses = (
		keys: readonly number[],
		action: BaselineAction,
		onSuccess?: (responseData: BaselineChangeStatusResponse) => void,
	): void => {
		const id = keys[0];
		if (id === undefined) {
			return;
		}
		items.value.forEach(item => {
			if (keys.includes(item.id)) {
				if (action === 'delete') {
					item.deleted = true;
					item.status = 'deleted';
				} else {
					item.deleted = false;
					item.status = 'updating';
				}
			}
		});
		ApiBaseline.baselineChangeStatus(id, action, handleChangeStatus(onSuccess));
	};

	const refresh = (): void => {
		isLoading.value = true;
		ApiBaseline.baselineGetList(responseData => {
			if (!Array.isArray(responseData.baselines) || responseData.settings == null) {
				isLoading.value = false;
				console.error('invalid baseline list response', {
					status: responseData.status,
					responseKeys: Object.keys(responseData),
				});
				if (responseData.errors != null) {
					showErrorMessage(responseData.errors);
				}
				return;
			}
			update(responseData.baselines, responseData.settings);
		});
	};

	const clear = (): void => {
		items.value = [];
		settings.value = {};
		isLoading.value = false;
	};

	const baselinesPerBaseStations: ComputedRef<Map<number, number[]>> = computed(() => {
		const result = new Map<number, number[]>();
		for (const baseline of items.value) {
			if (baseline.status === 'working') {
				appendBaselineId(result, baseline.base.id, baseline.id);
				appendBaselineId(result, baseline.rover.id, baseline.id);
			}
		}
		return result;
	});

	return {
		items,
		settings,
		isLoading,
		baselinesPerBaseStations,
		changeStatuses,
		refresh,
		clear,
	};
});
